import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Menu, X } from "lucide-react";
import { useGetStarted } from "@/components/GetStartedDialog";

const nav = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/work", label: "Work" },
  { to: "/locations", label: "Areas" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/audit", label: "Audit" },
];

const FOOTER_AREAS = [
  { to: "/web-design/inland-empire", label: "Inland Empire" },
  { to: "/web-design/riverside", label: "Riverside" },
  { to: "/web-design/temecula", label: "Temecula" },
  { to: "/web-design/rancho-cucamonga", label: "Rancho Cucamonga" },
  { to: "/web-design/orange-county", label: "Orange County" },
  { to: "/web-design/irvine", label: "Irvine" },
  { to: "/web-design/newport-beach", label: "Newport Beach" },
  { to: "/locations", label: "All areas" },
  { to: "/industries", label: "Industries" },
];

const SOCIALS = [
  { name: "Facebook",  href: "https://www.facebook.com/profile.php?id=61574907426390" },
  { name: "Instagram", href: "https://www.instagram.com/redkeywebdesign" },
  { name: "TikTok",    href: "https://www.tiktok.com/@redkeywebdesign" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const { open: openDialog } = useGetStarted();

  const homeFaq = useRef(null);
  useEffect(() => {
    document.getElementById("shell-h1")?.remove();
    homeFaq.current = homeFaq.current || document.getElementById("home-faq-ld");
    const node = homeFaq.current;
    if (!node) return;
    if (pathname === "/") { if (!node.isConnected) document.head.appendChild(node); }
    else node.remove();
  }, [pathname]);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  return (
    <div className="min-h-screen bg-paper text-ink flex flex-col">
      {/* Skip link for keyboard / screen-reader users */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[200] focus:bg-ink focus:text-paper focus:px-4 focus:py-2 focus:font-ui"
      >
        Skip to main content
      </a>

      {/* ---------- Header ---------- */}
      <header
        data-testid="site-header"
        className="sticky top-0 z-40 bg-paper/85 backdrop-blur-md border-b hairline"
      >
        <div className="mx-auto max-w-[1360px] px-6 md:px-10">
          <div className="flex items-center justify-between h-24 md:h-28">
            <Link
              to="/"
              data-testid="site-logo"
              aria-label="Redkey Web Design — Home"
              className="flex items-center gap-3 group"
            >
              <img
                src="/brand/redkey-logo.png"
                alt="Redkey Web Design"
                width="1081"
                height="320"
                fetchPriority="high"
                className="h-14 sm:h-16 md:h-20 w-auto"
              />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-10" aria-label="Primary">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`nav-link-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    [
                      "font-ui text-[15px] link-underline-grow",
                      isActive ? "text-ink" : "text-ink-soft hover:text-ink",
                    ].join(" ")
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={openDialog}
                data-testid="header-get-started"
                className="inline-flex items-center gap-2 bg-ink text-paper px-5 py-2.5 text-sm font-ui hover:bg-electric transition-colors duration-200"
              >
                Get Started
                <span aria-hidden>→</span>
              </button>
            </nav>

            {/* Mobile toggle */}
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden p-2 -mr-2 text-ink"
              aria-label={open ? "Close menu" : "Open menu"}
              data-testid="mobile-menu-toggle"
            >
              {open ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile nav panel */}
        {open && (
          <div className="md:hidden border-t hairline bg-paper" data-testid="mobile-menu">
            <nav className="px-6 py-6 flex flex-col gap-4" aria-label="Mobile">
              {nav.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    [
                      "font-display text-2xl",
                      isActive ? "text-ink" : "text-ink-soft",
                    ].join(" ")
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <button
                type="button"
                onClick={openDialog}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-ink text-paper px-4 py-3 font-ui"
                data-testid="mobile-get-started"
              >
                Get Started →
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* ---------- Main ---------- */}
      <main id="main-content" className="flex-1">{children}</main>

      {/* ---------- Footer ---------- */}
      <footer
        data-testid="site-footer"
        className="mt-24 border-t hairline bg-paper"
      >
        <div className="mx-auto max-w-[1360px] px-6 md:px-10 py-14 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <p className="eyebrow mb-4">Colophon</p>
              <h3 className="font-display text-3xl md:text-5xl max-w-[18ch]">
                Have a project in mind? Let&rsquo;s make something that lasts.
              </h3>
              <button
                type="button"
                onClick={openDialog}
                className="inline-flex items-center gap-3 mt-8 font-ui text-lg link-underline text-ink"
                data-testid="footer-get-started"
              >
                Get Started
                <span className="text-brand-red" aria-hidden>→</span>
              </button>
            </div>

            <div className="md:col-span-2">
              <p className="eyebrow mb-4">Redkey</p>
              <ul className="space-y-2 font-body">
                <li><NavLink to="/" className="link-underline-grow">Home</NavLink></li>
                <li><NavLink to="/services" className="link-underline-grow">Services</NavLink></li>
                <li><NavLink to="/work" className="link-underline-grow">Selected work</NavLink></li>
                <li><NavLink to="/blog" className="link-underline-grow">Blog</NavLink></li>
                <li><NavLink to="/about" className="link-underline-grow">About</NavLink></li>
                <li><NavLink to="/audit" className="link-underline-grow">Site audit</NavLink></li>
              </ul>
            </div>

            <div className="md:col-span-3">
              <p className="eyebrow mb-4">Areas we serve</p>
              <ul className="space-y-2 font-body">
                {FOOTER_AREAS.map((a) => (
                  <li key={a.to}>
                    <NavLink to={a.to} className="link-underline-grow" data-testid={`footer-area-${a.to.split("/").pop()}`}>{a.label}</NavLink>
                  </li>
                ))}
              </ul>
            </div>

            <div className="md:col-span-3">
              <p className="eyebrow mb-4">Elsewhere</p>
              <ul className="space-y-2 font-body">
                <li>
                  <a href="mailto:hello@redkeyweb.design" className="link-underline-grow text-electric-ink">
                    hello@redkeyweb.design
                  </a>
                </li>
                {SOCIALS.map((s) => (
                  <li key={s.name}>
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`social-${s.name.toLowerCase()}`}
                      className="link-underline-grow"
                    >
                      {s.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-14 pt-6 border-t hairline flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img
                src="/brand/favicon.png"
                alt=""
                width="24"
                height="24"
                loading="lazy"
                decoding="async"
                className="h-6 w-6"
              />
              <p className="font-ui text-[11px] text-muted-ink tracking-widest">
                © {new Date().getFullYear()} REDKEY WEB DESIGN — CUSTOM WEB DEVELOPMENT
              </p>
            </div>
            <p className="font-ui text-[11px] text-muted-ink tracking-widest">
              CRAFTED, NOT TEMPLATED.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
